import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ResourceVm } from 'qp-ui-interface';
import { Observable } from 'rxjs';
import { DataService } from 'src/app/data.service';
import {
  TAMIL,
  TAMIL_ACL,
  TELUGU,
  TELUGU_ACL,
} from 'src/app/shared/common/const/common.const';
import {
  SubscriptionStatus,
  UserSubscriptionStatus,
} from 'src/app/shared/enums/subscription';
import { getApiUrl } from 'src/app/shared/utils/apiUtils';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import {
  getAuthHeaders,
  getBaseRequestData,
} from 'src/app/shared/utils/evergentUtils';
import { ConfigService } from '../../config.service';
import { LocalStorageService } from '../../storage/local-storage.service';
import { AccountProfileService } from '../account/account-profile.service';
import { VODDetailsMessage } from './model/entitlement/vod-details.message';
import { GetVODEntitlementsRequestMessage } from './model/entitlement/vod-entitlement.request.message';
import { GetVODEntitlementsResponseMessage } from './model/entitlement/vod-entitlement.response.message';
import { UserEntitlementService } from './user-entitlement.service';

@Injectable({
  providedIn: 'root',
})
@Injectable()
export class ContentEntitlementService {
  /** List of purchased VOD content */
  private tvodPurchasedList: VODDetailsMessage[] = [];

  constructor(
    private readonly http: HttpClient,
    private readonly localStorage: LocalStorageService,
    private readonly accountProfileService: AccountProfileService,
    private readonly dataService: DataService,
    private readonly configService: ConfigService,
    private readonly userEntlmnt: UserEntitlementService
  ) {}

  getTvodPurchasedList(resourceVm: ResourceVm) {
    const tvodPurchasedList = JSON.parse(
      localStorage.getItem(KEYS.TVOD_PURCHASED_LIST)
    );
    const purchasedDetail: VODDetailsMessage[] | null =
      tvodPurchasedList?.filter((tvodPurchased: VODDetailsMessage) => {
        return tvodPurchased?.serviceID === resourceVm?.id;
      });
    return purchasedDetail;
  }

  isContentPurchased(resourceVm: ResourceVm) {
    return this.getTvodPurchasedList(resourceVm)?.length > 0;
  }

  get isPremium() {
    const resource = this.dataService.selectedCardData;
    return resource?.isPremium && !resource?.isTvod;
  }
  private getVODEntitlements(): Observable<GetVODEntitlementsResponseMessage> {
    const url = getApiUrl(
      this.configService.applicationConfig.UmsURL,
      'GET_VOD_ENTITLEMENTS'
    );
    const requestContent = getBaseRequestData(
      this.configService.applicationConfig
    );
    const requestMessage = new GetVODEntitlementsRequestMessage();

    requestMessage.GetVODEntitlementsRequestMessage = requestContent;

    return this.http.post<GetVODEntitlementsResponseMessage>(
      url,
      requestMessage,
      getAuthHeaders(this.localStorage)
    );
  }

  async fetchVodEntitlements() {
    const vodEntitlements = await this.getVODEntitlements().toPromise();
    return this.handleVODEntitlements(vodEntitlements);
  }

  public handleVODEntitlements(
    vodEntitlement: GetVODEntitlementsResponseMessage
  ) {
    this.tvodPurchasedList =
      vodEntitlement.GetVODEntitlementsResponseMessage?.VODDetailsMessage;

    if (this.tvodPurchasedList) {
      this.localStorage.setItem(
        KEYS.TVOD_PURCHASED_LIST,
        JSON.stringify(this.tvodPurchasedList)
      );
    } else if (
      vodEntitlement?.GetVODEntitlementsResponseMessage?.responseCode === '1'
    ) {
      this.localStorage.removeItem(KEYS.TVOD_PURCHASED_LIST);
    }

    if (vodEntitlement?.GetVODEntitlementsResponseMessage?.tvodToken) {
      this.localStorage.setItem(
        KEYS.TVOD_TOKEN,
        JSON.stringify(
          vodEntitlement.GetVODEntitlementsResponseMessage.tvodToken
        )
      );
    }
    return this.tvodPurchasedList?.length ? this.tvodPurchasedList : null;
  }

  canPlayVideo(resource: ResourceVm): SubscriptionStatus {
    const accountProfile =
      this.accountProfileService.account?.GetAccountProfileResponseMessage;
    const userEntitlementStatus = this.userEntlmnt.userSubscriptionStatus();
    const isSubscribed = accountProfile?.subscriptionStatus;

    if (this.isFreeContent(resource)) {
      return SubscriptionStatus.FREE_CONTENT;
    }

    const subscribedCatalogues = accountProfile?.subscribedCatalogue;
    const resourceAcl = resource.catalog.toLowerCase();

    // Check SVOD subscription eligibility
    const hasSVODSubscription =
      subscribedCatalogues?.length &&
      isSubscribed &&
      ((resourceAcl == TAMIL_ACL &&
        subscribedCatalogues.map((it) => it.toLowerCase()).includes(TAMIL)) ||
        (resourceAcl == TELUGU_ACL &&
          subscribedCatalogues.map((it) => it.toLowerCase()).includes(TELUGU)));

    // Check if the user has an opposite SVOD subscription
    const hasOppositeSVODSubscription =
      subscribedCatalogues?.length &&
      isSubscribed &&
      ((resourceAcl == TAMIL_ACL &&
        subscribedCatalogues.map((it) => it.toLowerCase()).includes(TELUGU)) ||
        (resourceAcl == TELUGU_ACL &&
          subscribedCatalogues.map((it) => it.toLowerCase()).includes(TAMIL)));

    // Check TVOD subscription eligibility
    const hasTVODSubscription = this.isContentPurchased(resource);

    // Free preview logic
    if (resource.freePreview) {
      if (hasSVODSubscription || hasTVODSubscription) {
        return SubscriptionStatus.FULLY_SUBSCRIBED;
      } else {
        return SubscriptionStatus.FREE_PREVIEW;
      }
    } else if (resource.isTvod) {
      if (hasTVODSubscription) {
        return SubscriptionStatus.TVOD_SUBSCRIBED;
      } else {
        return SubscriptionStatus.TVOD_NEEDS_SUBSCRIPTION;
      }
    } else {
      if (!subscribedCatalogues?.length) {
        return SubscriptionStatus.NEEDS_SUBSCRIPTION;
      } else if (hasSVODSubscription) {
        return SubscriptionStatus.FULLY_SUBSCRIBED;
      } else if (
        userEntitlementStatus == UserSubscriptionStatus.CANCELLED &&
        hasOppositeSVODSubscription
      ) {
        return SubscriptionStatus.NEEDS_SUBSCRIPTION;
      } else {
        return SubscriptionStatus.NEEDS_UPGRADE;
      }
    }
  }

  isContentPlayable(status: SubscriptionStatus): boolean {
    return (
      status == SubscriptionStatus.FREE_CONTENT ||
      status == SubscriptionStatus.FULLY_SUBSCRIBED ||
      status == SubscriptionStatus.FREE_PREVIEW ||
      status == SubscriptionStatus.TVOD_SUBSCRIBED
    );
  }

  private isFreeContent(resource: ResourceVm): boolean {
    return resource.entitlement?.some((e) =>
      e.subscriptionPackage?.some((p) => p.toLowerCase().includes('free'))
    );
  }

  isSVODContent(resource: ResourceVm): boolean {
    return !resource.isTvod && !this.isFreeContent(resource);
  }
}
